import { ActionBlock } from '@/components/product/ActionBlock';
import { PriceBlock } from '@/components/product/PriceBlock';
import { ProductPropsBadge } from '@/components/product/ProductPropsBadge';
import { cn } from '@/lib/utils';
import { useAuth } from '@/stores/authStore';
import { ProductListItem } from '@/types';
import { route } from 'ziggy-js';

interface MobileProductRowProps {
    variation: ProductListItem;
    isAvailable: boolean;
    handleAddToCart: (variation: ProductListItem) => void;
    onProductClick: (slug: string) => void;
    isInCart: boolean;
}

export function MobileProductRow({
    variation,
    isAvailable,
}: MobileProductRowProps) {
    const { isAuthenticated } = useAuth();

    const detailsArray = variation.details
        ? variation?.details.split('|').map((item) => item.trim())
        : [];

    const tareBadge = variation.packaging_type ?? null;
    const volumeBadge = variation.volume ? `${variation.volume}л` : null;

    return (
        <div
            className={cn(
                'flex flex-col rounded-md border border-zinc-800 bg-zinc-900/30 p-3 transition-colors',
                !isAvailable &&
                    'border-zinc-900 bg-black/20 opacity-60 grayscale',
            )}
        >
            <div className="mb-1">
                {variation.isNew && (
                    <span className="mb-0.5 inline-flex shrink-0 items-center self-start rounded-sm border border-cyan-600/50 bg-cyan-600 px-1.5 py-0.5 !text-[10px] text-xs font-bold uppercase tracking-wider text-white">
                        New
                    </span>
                )}
                {variation.isPromo && (
                    <span className="mb-0.5 inline-flex shrink-0 items-center self-start rounded-sm bg-brand-gold/20 px-1.5 py-0.5 text-[8px] font-bold uppercase tracking-wider text-brand-gold ring-1 ring-inset ring-brand-gold/30">
                        Акция
                    </span>
                )}
            </div>
            <div className="mb-3 flex gap-3">
                <a
                    href={route('product', { slug: variation.slug })}
                    className="block h-16 w-14 shrink-0 cursor-pointer overflow-hidden rounded-lg border border-zinc-800 bg-zinc-950"
                >
                    <img
                        src={variation.label}
                        alt={variation.name}
                        className="h-full w-full object-cover"
                        loading="lazy"
                    />
                </a>
                <a
                    className="block min-w-0 flex-1"
                    href={route('product', { slug: variation.slug })}
                >
                    <div className="mb-0.5 flex flex-col gap-2">
                        <p className="truncate text-sm font-bold uppercase tracking-widest text-brand-gold">
                            {variation.vendor}
                        </p>
                    </div>
                    <h3 className="mt-1 line-clamp-2 cursor-pointer text-xl font-bold leading-tight text-zinc-100">
                        {variation.name}
                    </h3>
                    <div className="mt-1 flex flex-wrap gap-1">
                        <ProductPropsBadge
                            className=" border border-zinc-800 bg-zinc-950/50 font-medium"
                            value={variation.style}
                        />
                        {detailsArray.length > 0 &&
                            detailsArray?.map((item) => (
                                <ProductPropsBadge key={item} value={item} />
                            ))}
                    </div>
                </a>
            </div>

            <div className="flex flex-col border-t border-zinc-800/50 pt-2">
                <div className="flex">
                    {variation.untappd && (
                        <ProductPropsBadge
                            value={variation.untappd.score}
                            untappd={true}
                        />
                    )}
                    {tareBadge && (
                        <ProductPropsBadge
                            value={tareBadge}
                            className="mr-1"
                            bgColorClass="border-zinc-700/50 bg-zinc-800/80"
                        />
                    )}
                    {volumeBadge && (
                        <ProductPropsBadge
                            value={volumeBadge}
                            bgColorClass="border-zinc-700/50 bg-zinc-800/80"
                        />
                    )}
                </div>
                <div className="mt-2 flex gap-2">
                    <div>
                        <PriceBlock variation={variation} />
                    </div>

                    {isAuthenticated && (
                        <div className="flex flex-1 flex-col justify-end">
                            <ActionBlock variation={variation} />
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
